import React from 'react';
import { TestResult } from '../services/resultService';
import dndData from '../data/full_dnd_structure.json';
import psychometricsData from '../data/psychometrics.json';
import Card from './Card';
import ScoreChart from './ScoreChart';
import { QuestionSchema } from '../types/question.schema';
import { CalculatedResults } from '../utils/calculations';
import { safeEvaluate } from '../utils/calculations';

type Question = QuestionSchema[0];

// Define types for psychometrics data
export interface PsychometricProfile {
  name: string;
  criteria: string;
  description: {
    emoji?: string;
    profile?: string;
    keyFeatures?: string[];
    strengths?: string[];
    challenges?: string[];
    recommendations?: string[];
  };
}

export interface PsychometricsData {
  profiles: Record<string, PsychometricProfile>;
  scales: Record<string, { name: string; description: string }>;
}

interface ClusterDisplayProps {
  results: CalculatedResults;
  questionsData: Question[];
}

interface DisplayMetric {
  name: string;
  description: string;
  value: number;
  formula: string;
}

// Вынесено за пределы компонента для корректных зависимостей
const ALL_ALIGNMENT_VARS = [
  'empathy', 'psychopathy', 'suppression', 'masking', 'defensiveHardness', 'attention',
  'dissociation', 'internalDissonance', 'affectiveIsolationScale', 'emotionalClimate',
  'affectiveDisruptionIndex', 'childhoodAlienation', 'ace', 'flattening', 'lie', 'r_metric'
];

const ClusterDisplay: React.FC<ClusterDisplayProps> = ({ results, questionsData }) => {
  const scores = React.useMemo(() => results.scores || {}, [results.scores]);

  // R-Метрика по формуле из psychometrics.json
  const rMetric = React.useMemo(() => {
    if (!scores) return null;
    const empathy = typeof scores.empathy === 'number' ? scores.empathy : 0;
    const suppression = typeof scores.suppression === 'number' ? scores.suppression : 0;
    const dissociation = typeof scores.dissociation === 'number' ? scores.dissociation : 0;
    const psychopathy = typeof scores.psychopathy === 'number' ? scores.psychopathy : 0;
    return (empathy - suppression - dissociation + 50) * (1 - psychopathy / 100);
  }, [scores]);

  const extendedMetrics = React.useMemo(() => {
    if (!scores) return {};
    const calculatedMetrics: { [key: string]: DisplayMetric } = {};
    for (const [key, metric] of Object.entries(psychometricsData.extended_metrics)) {
      const metricDef = metric as { formula: string; description: string };
      const value = safeEvaluate(metricDef.formula, scores, null);
      if (typeof value === 'number') {
        calculatedMetrics[key] = {
          name: (psychometricsData.scales[key as keyof typeof psychometricsData.scales] as any)?.name || key,
          description: metricDef.description,
          value: value,
          formula: metricDef.formula,
        };
      }
    }
    return calculatedMetrics;
  }, [scores]);

  // enhancedScope: всегда содержит все переменные из ALL_ALIGNMENT_VARS, extended metrics и scores (0 по умолчанию)
  const enhancedScope = React.useMemo(() => {
    const scope: Record<string, number> = {};
    // 1. Все переменные из scores (camelCase)
    for (const key of Object.keys(scores)) {
      const v = scores[key];
      scope[key] = typeof v === 'number' && !isNaN(v) ? v : 0;
    }
    // 2. Все переменные из extendedMetrics
    for (const key of Object.keys(extendedMetrics)) {
      const v = (extendedMetrics as any)[key]?.value;
      scope[key] = typeof v === 'number' && !isNaN(v) ? v : 0;
    }
    // 3. Все переменные из ALL_ALIGNMENT_VARS (если их нет — 0)
    for (const v of ALL_ALIGNMENT_VARS) {
      if (!(v in scope)) scope[v] = 0;
    }
    // 4. r_metric всегда явно
    scope['r_metric'] = typeof rMetric === 'number' && !isNaN(rMetric) ? rMetric : 0;
    return scope;
  }, [scores, extendedMetrics, rMetric]);

  // Диагностика: проверка переменных для alignment
  function checkAlignmentVars(criteria: string, scope: Record<string, any>, label: string) {
    const varRegex = /([a-zA-Z_][a-zA-Z0-9_]*)/g;
    const usedVars = Array.from(new Set(criteria.match(varRegex)));
    for (const v of usedVars) {
      if (!(v in scope) || typeof scope[v] !== 'number' || isNaN(scope[v])) {
        // eslint-disable-next-line no-console
        console.warn(`[ALIGNMENT DIAG] Переменная '${v}' отсутствует или не число в scope для ${label}`);
      }
    }
  }

  const alignment = React.useMemo(() => {
    if (!scores) return { external: null, internal: null };
    let externalAlignment = null;
    let internalAlignment = null;
    // Проверяем все критерии и выводим диагностику
    for (const align of dndData.alignment.external) {
      checkAlignmentVars(align.criteria, enhancedScope, `external:${align.code}`);
      if (safeEvaluate(align.criteria, enhancedScope, false)) {
        externalAlignment = align;
        break;
      }
    }
    if (!externalAlignment && dndData.alignment.external.length > 0) {
      externalAlignment = dndData.alignment.external[dndData.alignment.external.length - 1];
      // eslint-disable-next-line no-console
      console.warn('[ALIGNMENT DIAG] Ни один критерий external не сработал, выбран fallback');
    }
    for (const align of dndData.alignment.internal) {
      checkAlignmentVars(align.criteria, enhancedScope, `internal:${align.code}`);
      if (safeEvaluate(align.criteria, enhancedScope, false)) {
        internalAlignment = align;
        break;
      }
    }
    if (!internalAlignment && dndData.alignment.internal.length > 0) {
      internalAlignment = dndData.alignment.internal[dndData.alignment.internal.length - 1];
      // eslint-disable-next-line no-console
      console.warn('[ALIGNMENT DIAG] Ни один критерий internal не сработал, выбран fallback');
    }
    return {
      external: externalAlignment,
      internal: internalAlignment,
    };
  }, [enhancedScope, scores]);

  const allMetricsForDisplay = React.useMemo(() => {
    const metrics: { [key: string]: DisplayMetric } = {
      'r_metric': {
        name: 'R-Метрика (реализованная эмпатия)',
        description: 'Индекс реализованной эмпатии (R) отражает, насколько человек способен не просто чувствовать, но и выражать эмпатию. Он рассчитывается по формуле: R = (Empathy − Suppression − Dissociation + 50) × (1 − Psychopathy / 100), где suppression и dissociation — эмоциональные защиты, а psychopathy — фактор морального отчуждения. Значение R выше 40 говорит о свободной реализации эмпатии, значения ниже 20 указывают на её блокировку, а отрицательные — на выраженное подавление и искажение.',
        value: rMetric !== null ? rMetric : 0,
        formula: '(empathy - suppression - dissociation + 50) * (1 - psychopathy / 100)',
      },
      ...extendedMetrics
    };
    return metrics;
  }, [rMetric, extendedMetrics]);

  const activePsychometricProfiles = React.useMemo(() => {
    if (!scores) return [];
    return Object.entries(psychometricsData.profiles)
      .map(([id, profileData]) => ({
        id,
        ...profileData,
        formula: profileData.criteria,
      }))
      .filter(profile => safeEvaluate(profile.criteria, enhancedScope, false));
  }, [enhancedScope, scores]);

  const dndArchetype = React.useMemo(() => {
    if (!scores) return null;
    return Object.values(dndData.archetypes).find(arch => safeEvaluate(arch.criteria, enhancedScope, false)) || null;
  }, [enhancedScope, scores]);

  if (Object.keys(scores).length === 0) {
    return (
      <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-inner text-center animate-fade-in">
        <h2 className="text-2xl font-semibold text-gray-700 mb-2 flex items-center justify-center gap-2">
          <span role="img" aria-label="no-data">📭</span> Нет данных для отображения
        </h2>
        <p className="text-gray-500">Результаты будут доступны после прохождения теста.</p>
      </div>
    );
  }

  // DEBUG-блок для диагностики (только в dev)
  const isDev = process.env.NODE_ENV !== 'production';

  return (
    <div className="p-4 sm:p-8 md:p-12 bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl shadow-xl animate-fade-in">
      {/* DEBUG-блок удалён */}
      <div className="max-w-4xl mx-auto space-y-16">
        {/* Psychometric Profiles */}
        {activePsychometricProfiles.length > 0 && (
          <section className="animate-slide-up">
            <h2 className="text-3xl font-extrabold text-indigo-800 mb-8 flex items-center gap-3">
              <span role="img" aria-label="profile">🧠</span> Психометрические профили
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {activePsychometricProfiles.map(profile => (
                <Card key={profile.id} title={`${profile.description.emoji || ''} ${profile.name}`} variant="info" subtitle={profile.description.profile}>
                  {profile.formula && (
                    <p className="text-xs text-gray-500 mt-2 mb-4 font-mono bg-gray-100 p-2 rounded-md break-all">Формула: {profile.formula}</p>
                  )}
                  <div className="grid grid-cols-1 gap-y-2 text-sm mt-2">
                    {profile.description.keyFeatures && <div>
                      <h5 className="font-semibold text-gray-800 mb-1">Ключевые черты:</h5>
                      <ul className="list-disc list-inside text-gray-600 space-y-1">
                        {profile.description.keyFeatures?.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>}
                    {profile.description.strengths && <div>
                      <h5 className="font-semibold text-green-700 mb-1">Сильные стороны:</h5>
                      <ul className="list-disc list-inside text-green-600 space-y-1">
                        {profile.description.strengths?.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>}
                    {profile.description.challenges && <div>
                      <h5 className="font-semibold text-red-700 mb-1">Вызовы:</h5>
                      <ul className="list-disc list-inside text-red-600 space-y-1">
                        {profile.description.challenges?.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>}
                    {profile.description.recommendations && <div>
                      <h5 className="font-semibold text-blue-700 mb-1">Рекомендации:</h5>
                      <ul className="list-disc list-inside text-blue-600 space-y-1">
                        {profile.description.recommendations?.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>}
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* R-Метрика — отдельный крупный блок */}
        {allMetricsForDisplay['r_metric'] && (
          <section className="animate-fade-in">
            <Card
              title={allMetricsForDisplay['r_metric'].name}
              variant="metric"
              className="mb-8 p-8 border-2 border-indigo-400 shadow-2xl"
              subtitle={allMetricsForDisplay['r_metric'].formula}
            >
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1 flex flex-col items-center md:items-start">
                  <span className="text-5xl font-extrabold text-indigo-700 mb-2">{allMetricsForDisplay['r_metric'].value.toFixed(2)}</span>
                  <span className="text-sm text-gray-500 mb-4">значение R</span>
                  <div className="text-gray-700 text-base leading-relaxed mb-2 whitespace-pre-line">
                    {allMetricsForDisplay['r_metric'].description}
                  </div>
                  <div className="mt-4 text-sm text-gray-600 bg-indigo-50 rounded-lg p-4">
                    <b>Интерпретация:</b>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><b>R &gt; 40</b> — свободная реализация эмпатии</li>
                      <li><b>R 20–40</b> — частичная блокировка эмпатии</li>
                      <li><b>R &lt; 20</b> — выраженная блокировка</li>
                      <li><b>R &lt; 0</b> — подавление и искажение эмпатии</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>
          </section>
        )}

        {/* Score Chart Section */}
        <Card title="Детальные баллы по шкалам" emoji="📊" variant="metric" className="mb-8 animate-fade-in">
          <ScoreChart results={results} questionsData={questionsData} />
        </Card>

        {/* Остальные метрики */}
        {Object.keys(allMetricsForDisplay).filter(key => key !== 'r_metric').length > 0 && (
          <section className="animate-slide-up">
            <h2 className="text-3xl font-extrabold text-green-800 mb-8 flex items-center gap-3">
              <span role="img" aria-label="metrics">📈</span> Метрики
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Object.entries(allMetricsForDisplay).filter(([key]) => key !== 'r_metric').map(([key, metric]: [string, DisplayMetric]) => (
                <Card key={metric.name} title={metric.name} variant="metric" subtitle={metric.description}>
                  <p className="text-3xl font-extrabold text-indigo-700 mb-2">{metric.value.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-2 font-mono bg-gray-100 p-2 rounded-md break-all">{metric.formula}</p>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* DnD Archetype and Alignment */}
        <section className="animate-slide-up">
          <h2 className="text-3xl font-extrabold text-purple-800 mb-8 flex items-center gap-3">
            <span role="img" aria-label="dnd">🧙‍♂️</span> DnD Характеристики
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {dndArchetype && (
              <Card title="Ваш DnD архетип" emoji={dndArchetype.emoji} variant="fantasy" subtitle={dndArchetype.name}>
                <p className="text-md text-gray-700 mt-1 mb-2">{dndArchetype.description}</p>
                {dndArchetype.criteria && (
                  <div className="mt-2 text-xs text-purple-700 bg-purple-50 rounded p-2 font-mono break-all">
                    Формула: <span className="font-semibold">{dndArchetype.criteria}</span>
                  </div>
                )}
              </Card>
            )}
            {alignment.external && alignment.internal ? (
              <Card 
                title="Мировоззрение" 
                emoji="⚖️" 
                variant="fantasy"
              >
                <div className="flex flex-col gap-4">
                  <div className="bg-purple-50 rounded-lg p-4 mb-2">
                    <div className="font-bold text-purple-800 mb-1">Внешнее: {alignment.external.code ?? alignment.external.name ?? ''}</div>
                    <div className="text-xs text-purple-700 font-mono mb-2">
                      Формула: <span className="font-semibold">{alignment.external.criteria}</span>
                    </div>
                    <div className="text-sm text-gray-600">{alignment.external.description}</div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4 mb-2">
                    <div className="font-bold text-indigo-800 mb-1">Внутреннее: {alignment.internal.code ?? alignment.internal.name ?? ''}</div>
                    <div className="text-xs text-indigo-700 font-mono mb-2">
                      Формула: <span className="font-semibold">{alignment.internal.criteria}</span>
                    </div>
                    <div className="text-sm text-gray-600">{alignment.internal.description}</div>
                  </div>
                </div>
              </Card>
            ) : <p className="text-gray-500">Мировоззрение не определено</p>}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ClusterDisplay;